/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useEffect, useState } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  DropDown,
  Icons,
  Input,
  NumericInput,
  Text,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { Color } from '../../../form';
import Tooltip from '../../../tooltip';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import { getCommonValue, inputContainerStyleOverride } from '../../shared';
import useRichText from '../../../richText/useRichText';
import {
  getNormalizedTextListStyle,
  setTextListMarkerSize,
  hasSemanticList,
  isSingleGrapheme,
} from '../../../../elements/text/utils/listStyle';
import { TEXT_LIST_STYLE_LIMITS } from '../../../../elements/text/constants';
import getListMarkerColor from '../../../../elements/text/utils/getListMarkerColor';

const MARKER_OPTIONS = [
  { label: __('Disc', 'web-stories'), value: 'disc' },
  { label: __('Circle', 'web-stories'), value: 'circle' },
  { label: __('Square', 'web-stories'), value: 'square' },
  { label: __('Custom', 'web-stories'), value: 'custom' },
];

const DEFAULT_CUSTOM_MARKER_GLYPH = '•';
const getValidCustomMarkerGlyph = (value) =>
  isSingleGrapheme(value) ? value : DEFAULT_CUSTOM_MARKER_GLYPH;
export const LIST_SETTINGS_NESTED_POPUP_ID = 'text-list-settings-nested-popup';

const Section = styled.section`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionTitle = styled(Text)`
  display: block;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 10px;
  margin-bottom: 12px;
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const CustomMarkerRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 41fr) minmax(0, 59fr);
  gap: 10px;
`;

const StyledDropDown = styled(DropDown)`
  width: 100%;
`;

const ControlWithAction = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
`;

function CustomGlyphInput({ glyph, onChange }) {
  const [draftGlyph, setDraftGlyph] = useState(() =>
    getValidCustomMarkerGlyph(glyph)
  );

  useEffect(() => {
    setDraftGlyph(getValidCustomMarkerGlyph(glyph));
  }, [glyph]);

  const handleChange = (evt) => {
    const value = evt.target.value;
    if (value !== '' && !isSingleGrapheme(value)) {
      return;
    }

    setDraftGlyph(value);
    if (value) {
      onChange(value, false);
    }
  };

  const handleBlur = () => {
    const value = getValidCustomMarkerGlyph(draftGlyph);
    setDraftGlyph(value);
    onChange(value, true);
  };

  return (
    <Input
      aria-label={__('Custom marker', 'web-stories')}
      value={draftGlyph}
      suffix={__('Glyph', 'web-stories')}
      onChange={handleChange}
      onBlur={handleBlur}
      containerStyleOverride={inputContainerStyleOverride}
    />
  );
}

CustomGlyphInput.propTypes = {
  glyph: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

function ListControls({ selectedElements, pushUpdate, onRemoveList }) {
  const { hasCurrentEditor, selectionHasList } = useRichText(
    ({ state: { hasCurrentEditor, selectionInfo } }) => ({
      hasCurrentEditor,
      selectionHasList: selectionInfo?.hasList || false,
    })
  );
  const allSelectedElementsHaveLists =
    selectedElements.length > 0 &&
    selectedElements.every(({ content }) => hasSemanticList(content));

  if (
    (hasCurrentEditor && !selectionHasList) ||
    (!hasCurrentEditor && !allSelectedElementsHaveLists)
  ) {
    return null;
  }

  const normalizedElements = selectedElements.map((element) => ({
    element,
    listStyle: getNormalizedTextListStyle(element.listStyle, element.fontSize),
  }));
  const markerPreset = getCommonValue(
    normalizedElements,
    ({ listStyle }) => listStyle.marker.preset
  );
  const markerGlyph = getCommonValue(
    normalizedElements,
    ({ listStyle }) => listStyle.marker.glyph
  );
  const markerSizeOverride = getCommonValue(
    normalizedElements,
    ({ element }) => element.listStyle?.marker?.size
  );
  const markerSize =
    markerSizeOverride === undefined
      ? getCommonValue(selectedElements, 'fontSize')
      : markerSizeOverride;
  const markerColor = getCommonValue(selectedElements, getListMarkerColor);
  const markerBaselineShift = getCommonValue(
    normalizedElements,
    ({ listStyle }) => listStyle.marker.baselineShift || 0
  );
  const markerHorizontalShift = getCommonValue(
    normalizedElements,
    ({ listStyle }) => listStyle.marker.horizontalShift || 0
  );
  const markerSizeIsInherited = normalizedElements.every(
    ({ element }) => !Number.isFinite(element.listStyle?.marker?.size)
  );
  const markerColorIsInherited = normalizedElements.every(
    ({ element }) => !element.listStyle?.marker?.color
  );

  const updateListStyle = (updater, submit = true) => {
    pushUpdate(
      (element) => ({
        listStyle: updater(
          getNormalizedTextListStyle(element.listStyle, element.fontSize),
          element
        ),
      }),
      submit
    );
  };

  const updateMarker = (updater, submit = true) =>
    updateListStyle(
      (listStyle, element) => ({
        ...listStyle,
        marker: updater(listStyle.marker, element),
      }),
      submit
    );

  const renderNumericInput = (property, label, limits) => {
    const value = getCommonValue(
      normalizedElements,
      ({ listStyle }) => listStyle[property]
    );
    const update = (newValue, submit) =>
      updateListStyle(
        (listStyle) => ({ ...listStyle, [property]: newValue }),
        submit
      );

    return (
      <NumericInput
        aria-label={label}
        value={value === MULTIPLE_VALUE ? '' : value}
        placeholder={
          value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : undefined
        }
        isIndeterminate={value === MULTIPLE_VALUE}
        min={limits.MIN}
        max={limits.MAX}
        suffix={label}
        onChange={(evt, newValue) => update(newValue, false)}
        onBlur={(evt, newValue) => update(newValue, true)}
        containerStyleOverride={inputContainerStyleOverride}
      />
    );
  };

  return (
    <Section aria-label={__('List settings', 'web-stories')}>
      <Header>
        <SectionTitle>{__('Bulleted list', 'web-stories')}</SectionTitle>
        <Tooltip hasTail title={__('Remove bulleted list', 'web-stories')}>
          <Button
            aria-label={__('Remove bulleted list', 'web-stories')}
            size={BUTTON_SIZES.SMALL}
            type={BUTTON_TYPES.TERTIARY}
            variant={BUTTON_VARIANTS.SQUARE}
            onClick={onRemoveList}
          >
            <Icons.Trash />
          </Button>
        </Tooltip>
      </Header>
      <Grid>
        <FullWidth>
          <StyledDropDown
            ariaLabel={__('Marker style', 'web-stories')}
            dropDownLabel={__('Marker', 'web-stories')}
            options={MARKER_OPTIONS}
            popupId={LIST_SETTINGS_NESTED_POPUP_ID}
            selectedValue={markerPreset === MULTIPLE_VALUE ? '' : markerPreset}
            placeholder={
              markerPreset === MULTIPLE_VALUE
                ? MULTIPLE_DISPLAY_VALUE
                : MARKER_OPTIONS[0].label
            }
            onMenuItemClick={(evt, value) =>
              updateMarker((marker) => ({
                ...marker,
                preset: value,
                ...(value === 'custom'
                  ? { glyph: marker.glyph || DEFAULT_CUSTOM_MARKER_GLYPH }
                  : { glyph: undefined, horizontalShift: undefined }),
              }))
            }
          />
        </FullWidth>
        {markerPreset === 'custom' && (
          <CustomMarkerRow>
            <CustomGlyphInput
              glyph={markerGlyph}
              onChange={(glyph, submit) =>
                updateMarker(
                  (marker) => ({
                    ...marker,
                    preset: 'custom',
                    glyph,
                  }),
                  submit
                )
              }
            />
            <NumericInput
              aria-label={__('Marker horizontal shift', 'web-stories')}
              value={
                markerHorizontalShift === MULTIPLE_VALUE
                  ? ''
                  : markerHorizontalShift
              }
              placeholder={
                markerHorizontalShift === MULTIPLE_VALUE
                  ? MULTIPLE_DISPLAY_VALUE
                  : undefined
              }
              isIndeterminate={markerHorizontalShift === MULTIPLE_VALUE}
              canBeNegative
              min={TEXT_LIST_STYLE_LIMITS.markerHorizontalShift.MIN}
              max={TEXT_LIST_STYLE_LIMITS.markerHorizontalShift.MAX}
              suffix={__('Horizontal shift', 'web-stories')}
              onChange={(evt, value) =>
                updateMarker(
                  (marker) => ({ ...marker, horizontalShift: value }),
                  false
                )
              }
              onBlur={(evt, value) =>
                updateMarker((marker) => ({
                  ...marker,
                  horizontalShift: value,
                }))
              }
              containerStyleOverride={inputContainerStyleOverride}
            />
          </CustomMarkerRow>
        )}
        <ControlWithAction>
          <Color
            value={markerColor}
            allowsGradient={false}
            allowsOpacity
            onChange={(value) =>
              updateMarker((marker) => ({ ...marker, color: value }))
            }
            label={__('Marker color', 'web-stories')}
          />
          <Tooltip hasTail title={__('Use text color', 'web-stories')}>
            <Button
              aria-label={__('Use text color', 'web-stories')}
              disabled={markerColorIsInherited}
              size={BUTTON_SIZES.SMALL}
              type={BUTTON_TYPES.TERTIARY}
              variant={BUTTON_VARIANTS.SQUARE}
              onClick={() =>
                updateMarker((marker) => ({ ...marker, color: undefined }))
              }
            >
              <Icons.Pipette />
            </Button>
          </Tooltip>
        </ControlWithAction>
        <ControlWithAction>
          <NumericInput
            aria-label={__('Marker size', 'web-stories')}
            value={markerSize === MULTIPLE_VALUE ? '' : markerSize}
            placeholder={
              markerSize === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : undefined
            }
            isIndeterminate={markerSize === MULTIPLE_VALUE}
            min={TEXT_LIST_STYLE_LIMITS.markerSize.MIN}
            max={TEXT_LIST_STYLE_LIMITS.markerSize.MAX}
            suffix={__('Size', 'web-stories')}
            onChange={(evt, value) =>
              updateMarker(
                (marker) => setTextListMarkerSize(marker, value),
                false
              )
            }
            onBlur={(evt, value) =>
              updateMarker((marker) => setTextListMarkerSize(marker, value))
            }
            containerStyleOverride={inputContainerStyleOverride}
          />
          <Tooltip hasTail title={__('Use text size', 'web-stories')}>
            <Button
              aria-label={__('Use text size', 'web-stories')}
              disabled={markerSizeIsInherited}
              size={BUTTON_SIZES.SMALL}
              type={BUTTON_TYPES.TERTIARY}
              variant={BUTTON_VARIANTS.SQUARE}
              onClick={() =>
                updateMarker((marker) =>
                  setTextListMarkerSize(marker, undefined)
                )
              }
            >
              <Icons.LetterAHeight />
            </Button>
          </Tooltip>
        </ControlWithAction>
        <NumericInput
          aria-label={__('Marker baseline shift', 'web-stories')}
          value={
            markerBaselineShift === MULTIPLE_VALUE ? '' : markerBaselineShift
          }
          placeholder={
            markerBaselineShift === MULTIPLE_VALUE
              ? MULTIPLE_DISPLAY_VALUE
              : undefined
          }
          isIndeterminate={markerBaselineShift === MULTIPLE_VALUE}
          canBeNegative
          min={TEXT_LIST_STYLE_LIMITS.markerBaselineShift.MIN}
          max={TEXT_LIST_STYLE_LIMITS.markerBaselineShift.MAX}
          suffix={__('Baseline shift', 'web-stories')}
          onChange={(evt, value) =>
            updateMarker(
              (marker) => ({ ...marker, baselineShift: value }),
              false
            )
          }
          onBlur={(evt, value) =>
            updateMarker((marker) => ({ ...marker, baselineShift: value }))
          }
          containerStyleOverride={inputContainerStyleOverride}
        />
        {renderNumericInput(
          'markerGap',
          __('Marker gap', 'web-stories'),
          TEXT_LIST_STYLE_LIMITS.markerGap
        )}
        {renderNumericInput(
          'indent',
          __('List indent', 'web-stories'),
          TEXT_LIST_STYLE_LIMITS.indent
        )}
        {renderNumericInput(
          'nestedIndent',
          __('Nested indent', 'web-stories'),
          TEXT_LIST_STYLE_LIMITS.nestedIndent
        )}
        <FullWidth>
          {renderNumericInput(
            'itemSpacing',
            __('Item spacing', 'web-stories'),
            TEXT_LIST_STYLE_LIMITS.itemSpacing
          )}
        </FullWidth>
      </Grid>
    </Section>
  );
}

ListControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
  onRemoveList: PropTypes.func.isRequired,
};

export default ListControls;
